import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addFlattenDraftPoint, FlattenArea, resetFlattenDraft, setFlattenPolygon } from 'store/slices/TreeSlice';
import { selectIsFlattenAreaMode, selectFlattenShape } from 'store/slices/TreeSlice/selector';
import { POLYGON_STROKE } from './FlattenAreaPolygon';
import { getImagePointFromEvent, shouldBlockNewShape, useFlattenAreaGuardRefs, usePinchAwareTap } from './flattenAreaToolUtils';
import PolygonHandle from './PolygonHandle';
import { pointsToPolygon } from 'components/tool/utils';
import { closedArcLength, Pt, resampleAtEqualArcLength, toFlat, toPts } from './freeDrawUtils';

const CLOSE_DISTANCE_PX = 8;
const MIN_SEGMENT_PX = 8;
const MAX_COMMITTED_POINTS = 24;
const MIN_COMMITTED_POINTS = 3;
const MIN_POINT_GAP_PX = 15;

export default function FlattenAreaFreeDrawTool() {
	const dispatch = useDispatch();
	const { konvaRef } = useRefs();
	const baseImageScale = useKonvaImageScale();

	const { treeEditingMode } = useSelector((state: RootState) => state.treeSlice.data);
	const { moveStage, rotateStage, editModeEnabled, threeDModelEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const flattenShape = useSelector(selectFlattenShape);
	const draftPoints = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points);
	const { selectedShapeIdRef, flattenAreaPolygonsRef, loadingFlattenIdsRef } = useFlattenAreaGuardRefs();

	const [points, setPoints] = useState<number[]>([]);
	const drawingIdRef = useRef<string | null>(null);
	const drawingRef = useRef(false);
	const pointsRef = useRef<number[]>([]);
	const lastRef = useRef<Pt | null>(null);
	const startRef = useRef<Pt | null>(null);
	const hasLeftStartAreaRef = useRef(false);

	const canEdit = editModeEnabled && treeEditingMode && isFlattenAreaMode && !threeDModelEnabled && flattenShape === 'free';
	const canDraw = canEdit && !moveStage && !rotateStage;

	// True until the first point reaches Redux — stops the sync effect below from resetting mid-draw.
	const pendingFirstPointRef = useRef(false);

	const getImagePoint = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => (
		getImagePointFromEvent(e, baseImageScale)
	), [baseImageScale]);

	const resetLocal = useCallback(() => {
		drawingRef.current = false;
		drawingIdRef.current = null;
		pendingFirstPointRef.current = false;
		pointsRef.current = [];
		startRef.current = null;
		lastRef.current = null;
		hasLeftStartAreaRef.current = false;
		setPoints([]);
	}, []);

	const commit = useCallback(() => {
		const id = drawingIdRef.current;
		if (!id) return;
		const current = pointsRef.current.slice();
		if (current.length < 6) return;

		const raw = toPts(current);
		const startPt = raw[0];
		let trimEnd = raw.length;
		while (trimEnd > 3 && Math.hypot(raw[trimEnd - 1].x - startPt.x, raw[trimEnd - 1].y - startPt.y) <= CLOSE_DISTANCE_PX) {
			trimEnd--;
		}
		const trimmed = trimEnd < raw.length ? raw.slice(0, trimEnd) : raw;
		const targetCount = Math.max(MIN_COMMITTED_POINTS, Math.min(MAX_COMMITTED_POINTS, Math.floor(closedArcLength(trimmed) / MIN_POINT_GAP_PX)));
		const resampled = resampleAtEqualArcLength([...trimmed, trimmed[0]], targetCount + 1).slice(0, targetCount);

		const flat = toFlat(resampled);
		if (flat.length < 6) return;

		const polygonCoords = pointsToPolygon(flat);
		const flattenAreaPolygon: FlattenArea = {
			coords: polygonCoords,
			id,
			flat_px: null,
			facet_affected_px: null,
			ground_z: 0,
			height: 0,
			shape: 'free',
		};
		
		dispatch(setFlattenPolygon(flattenAreaPolygon));
		resetLocal();
	}, [dispatch, resetLocal]);
	
	const maybeCommitNearStart = useCallback((p: Pt) => {
		const start = startRef.current;
		if (!start) return false;
		if (pointsRef.current.length < 6) return false;
		const dist = Math.hypot(p.x - start.x, p.y - start.y);
		if (dist > CLOSE_DISTANCE_PX) return false;
		commit();
		return true;
	}, [commit]);

	// The actual tap action: close the shape near its start point, else start a new one.
	const performTap = useCallback((point: Pt) => {
		if (drawingRef.current) {
			maybeCommitNearStart(point); // ignored if not near the start point
			return;
		}

		if (shouldBlockNewShape(
			dispatch, point, selectedShapeIdRef.current, flattenAreaPolygonsRef.current, loadingFlattenIdsRef.current
		)) return;

		drawingRef.current = true;
		drawingIdRef.current = crypto.randomUUID();
		startRef.current = point;
		lastRef.current = point;
		pointsRef.current = [point.x, point.y];
		setPoints(pointsRef.current.slice());
		pendingFirstPointRef.current = true;
		dispatch(addFlattenDraftPoint({ point }));
	}, [dispatch, maybeCommitNearStart, selectedShapeIdRef, flattenAreaPolygonsRef, loadingFlattenIdsRef]);

	const {
		isPinchingRef, isConcurrentFinger, startTap, clearPointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd,
	} = usePinchAwareTap(performTap);

	const handlePointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!canDraw) return;
		if (isConcurrentFinger(e)) return; // 2nd, concurrent finger — shape stays exactly as is
		const point = getImagePoint(e);
		if (!point) return;
		startTap(e, point);
	}, [canDraw, getImagePoint, isConcurrentFinger, startTap]);

	const handlePointerMove = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!canDraw) return;
		if (!drawingRef.current) return;
		if (isPinchingRef.current) return; // pinch — ignore its move noise
		const point = getImagePoint(e);
		if (!point) return;

		const start = startRef.current;
		if (start && !hasLeftStartAreaRef.current) {
			const distToStart = Math.hypot(point.x - start.x, point.y - start.y);
			if (distToStart > CLOSE_DISTANCE_PX * 2) {
				hasLeftStartAreaRef.current = true;
			}
		}

		if (hasLeftStartAreaRef.current && maybeCommitNearStart(point)) return;

		const last = lastRef.current;
		if (last) {
			const dist = Math.hypot(point.x - last.x, point.y - last.y);
			if (dist < MIN_SEGMENT_PX) return;
		}

		lastRef.current = point;
		pointsRef.current.push(point.x, point.y);
		setPoints(pointsRef.current.slice());
	}, [canDraw, getImagePoint, maybeCommitNearStart, isPinchingRef]);

	// Free-draw commits via proximity-to-start, not release — no extra logic needed on release.
	const handlePointerUp = clearPointerUp;

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage || !canDraw) return;

		stage.on('pointerdown', handlePointerDown);
		stage.on('pointermove', handlePointerMove);
		stage.on('pointerup', handlePointerUp);
		stage.on('touchstart', handleTouchStart);
		stage.on('touchmove', handleTouchMove);
		stage.on('touchend', handleTouchEnd);
		stage.on('touchcancel', handleTouchEnd);

		return () => {
			stage.off('pointerdown', handlePointerDown);
			stage.off('pointermove', handlePointerMove);
			stage.off('pointerup', handlePointerUp);
			stage.off('touchstart', handleTouchStart);
			stage.off('touchmove', handleTouchMove);
			stage.off('touchend', handleTouchEnd);
			stage.off('touchcancel', handleTouchEnd);
		};
	}, [
		canDraw, handlePointerDown, handlePointerMove, handlePointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd, konvaRef,
	]);

	useEffect(() => {
		if (canEdit) return;
		if (drawingRef.current) dispatch(resetFlattenDraft());
		resetLocal();
	}, [canEdit, dispatch, resetLocal]);

	useEffect(() => {
		// Keep local "in-progress" free-draw in sync with draftFlatten so undo/redo works.
		if (!canEdit) return;

		if (draftPoints.length < 2) {
			if (drawingRef.current && !pendingFirstPointRef.current) resetLocal(); // skip while first point in flight
			return;
		}
		pendingFirstPointRef.current = false;

		const x = draftPoints[0];
		const y = draftPoints[1];
		if (!drawingRef.current) {
			drawingRef.current = true;
			drawingIdRef.current = crypto.randomUUID();
			startRef.current = { x, y };
			lastRef.current = { x, y };
			pointsRef.current = [x, y];
			setPoints(pointsRef.current.slice());
			return;
		}

		// If local state got reset but draft is still present (redo), restore the minimal preview.
		if (pointsRef.current.length === 0) {
			startRef.current = { x, y };
			lastRef.current = { x, y };
			pointsRef.current = [x, y];
			setPoints(pointsRef.current.slice());
		}
	}, [canEdit, draftPoints, resetLocal]);

	useEffect(() => () => resetLocal(), [resetLocal]);

	const startPoint = useMemo(() => {
		if (!points.length) return null;
		return { x: points[0], y: points[1] };
	}, [points]);

	if (!canEdit) return null;
	if (!points.length) return null;

	return (
		<>
			<Line
				points={points}
				stroke={POLYGON_STROKE}
				strokeWidth={3}
				strokeScaleEnabled={false}
				closed={false}
				listening={false}
			/>
			{startPoint && (
				<PolygonHandle
					x={startPoint.x}
					y={startPoint.y}
					listening={false}
				/>
			)}		
		</>
	);
}
